import React from 'react';
import { Typography, FormGroup, FormControlLabel, Checkbox, Box, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ResponsiveDialog from '../UI/Dialogs/ResponsiveDialog';
import { formatFixed2 } from '../../utils/numberUtils';

const Title = ({ worktype, channel, task, textColor }) => (
    <Typography variant="h6" color={textColor}>
        {worktype} - {channel} - {task}
    </Typography>
);

const DisplayMessage = ({
    type,
    errors,
    differenceFromStrategicVolume,
    missingWorktypes,
    missingChannels,
    missingTasks,
    missingLocations,
    missingWorktypeLocations,
    missingWorkingUnits,
    missingWorkingUnitLocations,
    skipReasons,
}) => {
    const { t } = useTranslation('bidding');

    switch (type) {
        case 'validationErrors':
            return (
                <>
                    <Typography>{t('uploadRes.validationErrorsTitle')}</Typography>
                    {missingWorktypes?.map((item, idx) => (
                        <Typography key={`wt-${idx}`}>
                            {t('uploadRes.missingWorktype', { value: item || '-' })}
                        </Typography>
                    ))}
                    {missingChannels?.map((item, idx) => (
                        <Typography key={`ch-${idx}`}>
                            {t('uploadRes.missingChannel', { value: item || '-' })}
                        </Typography>
                    ))}
                    {missingTasks?.map((item, idx) => (
                        <Typography key={`task-${idx}`}>
                            {t('uploadRes.missingTask', { value: item || '-' })}
                        </Typography>
                    ))}
                    {missingLocations?.map((item, idx) => (
                        <Typography key={`loc-${idx}`}>
                            {t('uploadRes.missingLocation', { value: item || '-' })}
                        </Typography>
                    ))}
                    {missingWorktypeLocations?.map((item, idx) => (
                        <Typography key={`wtloc-${idx}`}>
                            {t('uploadRes.missingWorktypeLocation', { value: item || '-' })}
                        </Typography>
                    ))}
                    {missingWorkingUnits?.map((item, idx) => (
                        <Typography key={`wu-${idx}`}>
                            {t('uploadRes.missingWorkingUnit', { value: item || '-' })}
                        </Typography>
                    ))}
                    {missingWorkingUnitLocations?.map((item, idx) => (
                        <Typography key={`wul-${idx}`}>
                            {t('uploadRes.missingWorkingUnitLocation', { value: item || '-' })}
                        </Typography>
                    ))}
                </>
            );
        case 'errors':
            return (
                <>
                    <Typography>{t('uploadRes.errorsIntro')}</Typography>
                    <FormGroup>
                        <FormControlLabel
                            control={<Checkbox checked={errors.negativeValues} />}
                            label={t('uploadRes.errorNegative')}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={errors.decimalValues} />}
                            label={t('uploadRes.errorDecimal')}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={errors.valuesOutsideOpeningHours} />}
                            label={t('uploadRes.errorOutsideHours')}
                        />
                        <FormControlLabel
                            control={<Checkbox checked={errors.diffFromStrategicVolumeGreaterThan100} />}
                            label={t('uploadRes.errorDiffOver100')}
                        />
                    </FormGroup>
                </>
            );
        case 'warning':
            const percentage = differenceFromStrategicVolume?.percentage || 0;
            const comparisonText = percentage > 0 ? t('uploadRes.higher') : t('uploadRes.lower');
            const percentageRounded = formatFixed2(Math.abs(percentage));

            return (
                <>
                    <Typography>{t('uploadRes.success')}</Typography>
                    <Typography>
                        {t('uploadRes.warning', { percentage: percentageRounded, comparison: comparisonText })}
                    </Typography>
                </>
            );
        case 'success':
            return <Typography>{t('uploadRes.success')}</Typography>;
        case 'noNewTotals':
            return (
                <>
                    <Typography>{t('uploadRes.noNewTotals')}</Typography>
                    {skipReasons?.noOfferedReports > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {t('uploadRes.noOfferedReports')}
                        </Typography>
                    )}
                    {skipReasons?.noMatchingOfferedReport > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {t('uploadRes.noMatchingOfferedReport', { count: skipReasons.noMatchingOfferedReport })}
                        </Typography>
                    )}
                </>
            );
        case 'alreadyReleased':
            return <Typography>{t('uploadRes.alreadyReleased')}</Typography>;
        case 'completed':
            return <Typography>{t('bidding.planningCompleted')}</Typography>;
        default:
            return null;
    }
};

const getTextColor = ({
    displayErrors,
    displayWarning,
    displaySuccess,
    displayNoNewTotals,
    alreadyReleasedTotals,
    completedCalculation,
    hasValidationErrors,
}) => {
    if (hasValidationErrors) return 'red';
    if (completedCalculation) return 'red';
    if (displayErrors) return 'red';
    if (displayWarning || alreadyReleasedTotals) return 'orange';
    if (displaySuccess) return 'green';
    if (displayNoNewTotals) return 'primary';
};

const CorrectedTotalsUploadResDialog = ({ open, onClose, response }) => {
    const items = Array.isArray(response) ? response : [];
    return (
        <ResponsiveDialog open={open} onClose={onClose} isConfirmationDialog={false}>
            {items.map((workingUnitLoc, index) => {
                const {
                    errors,
                    differenceFromStrategicVolume,
                    alreadyReleasedTotals,
                    numOfNewTotals,
                    worktype,
                    channel,
                    task,
                    completedCalculation,
                    skipReasons,
                } = workingUnitLoc;

                const hasValidationErrors =
                    (errors?.missingWorktypes?.length || 0) > 0 ||
                    (errors?.missingChannels?.length || 0) > 0 ||
                    (errors?.missingTasks?.length || 0) > 0 ||
                    (errors?.missingLocations?.length || 0) > 0 ||
                    (errors?.missingWorktypeLocations?.length || 0) > 0 ||
                    (errors?.missingWorkingUnits?.length || 0) > 0 ||
                    (errors?.missingWorkingUnitLocations?.length || 0) > 0;
                const absDiffFromStrategicVolume = differenceFromStrategicVolume?.absolute
                    ? Math.abs(differenceFromStrategicVolume.absolute)
                    : 0;
                const absPercentageDiff = differenceFromStrategicVolume?.percentage
                    ? Math.abs(differenceFromStrategicVolume.percentage)
                    : 0;

                const displayErrors =
                    errors.negativeValues ||
                    errors.decimalValues ||
                    errors.valuesOutsideOpeningHours ||
                    errors.diffFromStrategicVolumeGreaterThan100;
                const displayWarning = numOfNewTotals > 0 && absDiffFromStrategicVolume <= 100 && absPercentageDiff > 5;
                const displaySuccess = !displayErrors && !displayWarning && numOfNewTotals > 0;
                const displayNoNewTotals =
                    !displayErrors && !displayWarning && !alreadyReleasedTotals && numOfNewTotals === 0;
                const textColor = getTextColor({
                    displayErrors,
                    displayWarning,
                    displaySuccess,
                    displayNoNewTotals,
                    alreadyReleasedTotals,
                    completedCalculation,
                    hasValidationErrors,
                });

                let messageType;
                if (hasValidationErrors) {
                    messageType = 'validationErrors';
                } else if (completedCalculation) {
                    messageType = 'completed';
                } else if (displayErrors) {
                    messageType = 'errors';
                } else if (displayWarning) {
                    messageType = 'warning';
                } else if (displaySuccess) {
                    messageType = 'success';
                } else if (displayNoNewTotals) {
                    messageType = 'noNewTotals';
                } else if (alreadyReleasedTotals) {
                    messageType = 'alreadyReleased';
                }

                return (
                    <Box key={index} sx={{ mb: 2 }}>
                        <Title worktype={worktype} channel={channel} task={task} textColor={textColor} />
                        <Box sx={{ p: 2 }}>
                            <DisplayMessage
                                type={messageType}
                                errors={errors}
                                differenceFromStrategicVolume={differenceFromStrategicVolume}
                                missingWorktypes={errors?.missingWorktypes}
                                missingChannels={errors?.missingChannels}
                                missingTasks={errors?.missingTasks}
                                missingLocations={errors?.missingLocations}
                                missingWorktypeLocations={errors?.missingWorktypeLocations}
                                missingWorkingUnits={errors?.missingWorkingUnits}
                                missingWorkingUnitLocations={errors?.missingWorkingUnitLocations}
                                skipReasons={skipReasons}
                            />
                        </Box>
                        <Divider sx={{ mt: 2 }} />
                    </Box>
                );
            })}
        </ResponsiveDialog>
    );
};

export default CorrectedTotalsUploadResDialog;
